import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import Select from "react-select";
import ActivosSelectReact from "jsx/components/CitoPlus/components/Activos/ActivosSelectReact";
import ProveedorFormModal from "./ProveedorFormModal";

const ProgramacionFormModal = ({
  show,
  onHide,
  onSubmit,
  isSubmitting = false,
  proveedores = [],
  onCreateProveedor,
  initialData = null,
  isEditing = false,
}) => {
  const [formData, setFormData] = useState({
    titulo: "",
    descripcion: "",
    tipo: "preventivo",
    frecuencia: "mensual",
    fecha_inicio: "",
    activo_uuid: "",
    responsable_tipo: "interno",
    proveedor_nombre: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [generalError, setGeneralError] = useState("");
  const [showProveedorModal, setShowProveedorModal] = useState(false);
  const [isSubmittingProveedor, setIsSubmittingProveedor] = useState(false);

  useEffect(() => {
    if (show) {
      if (initialData && isEditing) {
        setFormData({
          titulo: initialData.titulo || "",
          descripcion: initialData.descripcion || "",
          tipo: initialData.tipo || "preventivo",
          frecuencia: initialData.frecuencia || "mensual",
          fecha_inicio: initialData.fecha_inicio || "",
          activo_uuid: initialData.activo?.uuid || "",
          responsable_tipo: initialData.responsable_tipo || "interno",
          proveedor_nombre: initialData.proveedor_nombre || "",
        });
      } else {
        setFormData({
          titulo: "",
          descripcion: "",
          tipo: "preventivo",
          frecuencia: "mensual",
          fecha_inicio: "",
          activo_uuid: "",
          responsable_tipo: "interno",
          proveedor_nombre: "",
        });
      }
      setFormErrors({});
      setGeneralError("");
    }
  }, [show, initialData, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
    setGeneralError("");

    try {
      await onSubmit(formData);
    } catch (err) {
      console.error("Error en formulario de programación:", err);
      if (err.response?.data?.errors) {
        const errors = err.response.data.errors;
        const fieldErrors = {};
        let generalErr = "";
        errors.forEach((error) => {
          if (error.attr && error.attr !== "non_field_errors") {
            fieldErrors[error.attr] = error.detail;
          } else {
            generalErr += error.detail + " ";
          }
        });
        setFormErrors(fieldErrors);
        if (generalErr) setGeneralError(generalErr.trim());
      } else if (err.response?.data?.detail) {
        setGeneralError(err.response.data.detail);
      } else {
        setGeneralError("Error al guardar la programación. Intente nuevamente.");
      }
    }
  };

  const handleCreateProveedor = async (proveedorData) => {
    setIsSubmittingProveedor(true);
    try {
      const newProveedor = await onCreateProveedor(proveedorData);
      setFormData((prev) => ({ ...prev, proveedor_nombre: newProveedor.nombre }));
      setShowProveedorModal(false);
    } catch (err) {
      console.error("Error creando proveedor:", err);
      throw err;
    } finally {
      setIsSubmittingProveedor(false);
    }
  };

  const proveedorOptions = proveedores.map((prov) => ({
    value: prov.nombre,
    label: prov.nombre,
  }));

  const handleClose = () => {
    setFormErrors({});
    setGeneralError("");
    onHide();
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditing ? "Editar" : "Crear"} Programación de Mantenimiento
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {generalError && (
              <div className="alert alert-danger" role="alert">
                {generalError}
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Título *</Form.Label>
              <Form.Control
                type="text"
                name="titulo"
                value={formData.titulo}
                onChange={handleChange}
                placeholder="Ej: Mantenimiento mensual de sillas"
                required
                isInvalid={!!formErrors.titulo}
              />
              {formErrors.titulo && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.titulo}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Descripción del mantenimiento..."
                isInvalid={!!formErrors.descripcion}
              />
              {formErrors.descripcion && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.descripcion}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Tipo *</Form.Label>
                  <Form.Select
                    name="tipo"
                    value={formData.tipo}
                    onChange={handleChange}
                    isInvalid={!!formErrors.tipo}
                  >
                    <option value="preventivo">Preventivo</option>
                    <option value="correctivo">Correctivo</option>
                  </Form.Select>
                  {formErrors.tipo && (
                    <Form.Control.Feedback type="invalid">
                      {formErrors.tipo}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Frecuencia *</Form.Label>
                  <Form.Select
                    name="frecuencia"
                    value={formData.frecuencia}
                    onChange={handleChange}
                    isInvalid={!!formErrors.frecuencia}
                  >
                    <option value="unico">Único</option>
                    <option value="mensual">Mensual</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="anual">Anual</option>
                  </Form.Select>
                  {formErrors.frecuencia && (
                    <Form.Control.Feedback type="invalid">
                      {formErrors.frecuencia}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Fecha de Inicio *</Form.Label>
              <Form.Control
                type="date"
                name="fecha_inicio"
                value={formData.fecha_inicio}
                onChange={handleChange}
                required
                isInvalid={!!formErrors.fecha_inicio}
              />
              {formErrors.fecha_inicio && (
                <Form.Control.Feedback type="invalid">
                  {formErrors.fecha_inicio}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Activo *</Form.Label>
              <ActivosSelectReact
                value={formData.activo_uuid}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, activo_uuid: value }))
                }
                showLabel={false}
                placeholder="Seleccione un activo"
                allowAllOption={false}
              />
              {formErrors.activo_uuid && (
                <div className="text-danger small mt-1">{formErrors.activo_uuid}</div>
              )}
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Responsable</Form.Label>
                  <Form.Select
                    name="responsable_tipo"
                    value={formData.responsable_tipo}
                    onChange={handleChange}
                  >
                    <option value="interno">Interno</option>
                    <option value="proveedor">Proveedor</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <Form.Label className="mb-0">Proveedor</Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setShowProveedorModal(true)}
                      disabled={isSubmitting}
                      style={{ padding: "0.1rem 0.3rem", fontSize: "0.75rem" }}
                    >
                      <i className="bi bi-plus"></i> Nuevo
                    </Button>
                  </div>
                  <Select
                    options={proveedorOptions}
                    value={
                      proveedorOptions.find(
                        (opt) => opt.value === formData.proveedor_nombre
                      ) || null
                    }
                    onChange={(selected) =>
                      setFormData((prev) => ({
                        ...prev,
                        proveedor_nombre: selected ? selected.value : "",
                      }))
                    }
                    placeholder="Seleccione un proveedor"
                    isClearable
                    isDisabled={isSubmitting}
                  />
                </Form.Group>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Guardando..." : isEditing ? "Actualizar" : "Guardar"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <ProveedorFormModal
        show={showProveedorModal}
        onHide={() => setShowProveedorModal(false)}
        onSubmit={handleCreateProveedor}
        isSubmitting={isSubmittingProveedor}
      />
    </>
  );
};

export default ProgramacionFormModal;
